<script setup lang="ts">
import { PageUrl } from '~/types'
import Trustpilot from '~/components/Trustpilot.vue'
import SafeHtml from '../UI/SafeHtml.vue'

const { footer } = useFooterStore()

const pageUrl = PageUrl

// L'anno del copyright non può essere dinamico dentro un campo Prismic, quindi
// si calcola qui e si affianca al testo statico scritto in CMS
const currentYear = new Date().getFullYear()

// WCAG 1.4.3 — footer su bg-slate-950: il colore link di default (pensato per
// sfondi chiari) è illeggibile qui, serve una variante chiara per sfondo scuro
const darkLinkClass = 'font-bold text-pink-300 hover:text-pink-200 visited:text-violet-300'
</script>

<template>
  <footer class="relative mx-auto w-full flex flex-col">
    <div class="mx-auto w-full bg-slate-950">
      <div class="mx-auto w-screen px-4 py-8 xl:container md:py-16 xl:px-0">
        <div class="flex flex-col gap-8 text-sm md:flex-row md:justify-between">
          <div class="flex flex-col justify-center sm:justify-start items-center gap-8">
            <NuxtLink to="/">
              <img class="flex" src="/images/logo.png" height="100%" width="200" alt="RisparmiaTu - Vai alla homepage" />
            </NuxtLink>
            <div class="flex justify-center gap-4 md:justify-start">
              <NuxtLink v-if="footer?.x_url" :to="footer?.x_url" target="_blank" external>
                <img class="hover:brightness-200" src="/images/social/x.png" alt="" height="32" width="32" />
                <span class="sr-only text-gray-400">X account</span>
              </NuxtLink>
              <NuxtLink v-if="footer?.facebook_url" :to="footer?.facebook_url" target="_blank" external>
                <img class="hover:brightness-200" src="/images/social/facebook.png" alt="" height="32" width="32" />
                <span class="sr-only text-gray-400">Facebook account</span>
              </NuxtLink>
              <NuxtLink v-if="footer?.instagram_url" :to="footer?.instagram_url" target="_blank" external>
                <img class="hover:brightness-200" src="/images/social/instagram.png" alt="" height="32" width="32" />
                <span class="sr-only text-gray-400">Instagram account</span>
              </NuxtLink>
              <NuxtLink v-if="footer?.whatsapp_url" :to="footer?.whatsapp_url" target="_blank" external>
                <img class="hover:brightness-200" src="/images/social/whatsapp.png" alt="" height="32" width="32" />
                <span class="sr-only text-gray-400">Whatsapp account</span>
              </NuxtLink>
            </div>
            <div class="flex w-fit justify-center md:justify-start gap-4">
              <Trustpilot />
            </div>
          </div>
          <div class="flex w-full flex-col gap-8 md:max-w-xs">
            <div
              class="inline-flex flex-wrap gap-1 justify-start items-start text-left text-gray-400">

              <SafeHtml v-if="footer?.contacts" :raw-html="footer?.contacts" tag="span" :link-class="darkLinkClass" linkify-phone-numbers />
              <SafeHtml v-if="footer?.working_hours" :raw-html="footer?.working_hours" tag="span" :link-class="darkLinkClass" />
            </div>
            <div class="text-left text-gray-400">
              <SafeHtml v-if="footer?.content" :raw-html="footer?.content" tag="span" :link-class="darkLinkClass" /><template
                v-if="footer?.contact_email"><br /><a class="font-bold text-gray-400 hover:text-gray-300 hover:underline" :href="`mailto:${footer?.contact_email}`"
                target="_blank">{{ footer?.contact_email }}</a></template><template
                v-if="footer?.rights_text"><br /><span>{{ `© ${currentYear} ` }}</span><SafeHtml :raw-html="footer?.rights_text" tag="span" unwrap-paragraphs :link-class="darkLinkClass" /></template>
            </div>
          </div>
          <div class="flex flex-col gap-8 sm:flex-row sm:flex-wrap sm:justify-around md:justify-end">
            <div class="flex flex-col justify-center gap-4 md:justify-start">
              <!-- WCAG 1.3.1 — p invece di h2: le sezioni footer non sono intestazioni di pagina -->
              <p class="text-sm text-[#AC5FE5] flex justify-start font-semibold">
                RisparmiaTu.com
              </p>
              <ul class="flex flex-col gap-1 font-medium text-gray-400">
                <li class="h-9 sm:h-auto flex justify-start items-center">
                  <NuxtLink :to="`/${pageUrl.ABOUT_US}`">
                    <div class="py-1 hover:text-gray-300 hover:underline flex justify-center md:justify-start">
                      Chi Siamo
                    </div>
                  </NuxtLink>
                </li>
                <li class="h-9 sm:h-auto flex justify-start items-center">
                  <NuxtLink :to="`/${pageUrl.HOW_IT_WORKS}`">
                    <div class="py-1 hover:text-gray-300 hover:underline flex justify-center md:justify-start">
                      Come Funziona
                    </div>
                  </NuxtLink>
                </li>
                <li class="h-9 sm:h-auto flex justify-start items-center">
                  <NuxtLink :to="`/${pageUrl.CONTACTS}`">
                    <div class="py-1 hover:text-gray-300 hover:underline flex justify-center md:justify-start">
                      Contatti
                    </div>
                  </NuxtLink>
                </li>
                              <li class="h-9 sm:h-auto flex justify-start items-center">
                  <NuxtLink :to="`/${pageUrl.WORK_WITH_US}`">
                    <div class="py-1 hover:text-gray-300 hover:underline flex justify-center md:justify-start">
                      Lavora con noi
                    </div>
                  </NuxtLink>
                </li>
                <li class="h-9 sm:h-auto flex justify-start items-center">
                  <NuxtLink :to="`/${pageUrl.TERMS_AND_CONDITIONS}`">
                    <div class="py-1 hover:text-gray-300 hover:underline flex justify-center md:justify-start">
                      Termini e Condizioni
                    </div>
                  </NuxtLink>
                </li>
                <li class="h-9 sm:h-auto flex justify-start items-center">
                  <NuxtLink :to="`/${pageUrl.PRIVACY_POLICY}`">
                    <div class="py-1 hover:text-gray-300 hover:underline flex justify-center md:justify-start">
                      Privacy Policy
                    </div>
                  </NuxtLink>
                </li>
                <li class="h-9 sm:h-auto flex justify-start items-center">
                  <NuxtLink :to="`/${pageUrl.COOKIE_POLICY}`">
                    <div class="py-1 hover:text-gray-300 hover:underline flex justify-center md:justify-start">
                      Cookie Policy
                    </div>
                  </NuxtLink>
                </li>
                <li class="h-9 sm:h-auto flex justify-start items-center">
                  <NuxtLink to="/accessibilita">
                    <div class="py-1 hover:text-gray-300 hover:underline flex justify-center md:justify-start">
                      Accessibilità
                    </div>
                  </NuxtLink>
                </li>
              </ul>
            </div>
            <div v-if="false" class="flex flex-col gap-4">
              <h2 class="text-sm text-gray-200 uppercase flex justify-center font-semibold md:justify-start">
                <NuxtLink :to="`/${pageUrl.BLOG}`" title="blog">
                  <div class="text-[#F2B71F] hover:underline flex justify-center md:justify-start">
                    Blog
                  </div>
                </NuxtLink>
              </h2>
              <ul class="flex flex-col gap-1 font-medium text-gray-500">
                <li class="h-12 sm:h-auto flex justify-start items-center">
                  <NuxtLink to="/" title="Ambiente">
                    <div class="py-1 hover:text-gray-300 hover:underline flex justify-center md:justify-start">
                      Ambiente
                    </div>
                  </NuxtLink>
                </li>
                <li class="h-12 sm:h-auto flex justify-start items-center">
                  <NuxtLink to="/" title="Guida e Consigli">
                    <div class="py-1 hover:text-gray-300 hover:underline flex justify-center md:justify-start">
                      Guida e Consigli
                    </div>
                  </NuxtLink>
                </li>
                <li class="h-12 sm:h-auto flex justify-start items-center">
                  <NuxtLink to="/" title="Ultime Offerte">
                    <div class="py-1 hover:text-gray-300 hover:underline flex justify-center md:justify-start">
                      Ultime Offerte
                    </div>
                  </NuxtLink>
                </li>
                <li class="h-12 sm:h-auto flex justify-start items-center">
                  <NuxtLink to="/" title="Teconologia">
                    <div class="py-1 hover:text-gray-300 hover:underline flex justify-center md:justify-start">
                      Tecnologia
                    </div>
                  </NuxtLink>
                </li>
                <li class="h-12 sm:h-auto flex justify-start items-center">
                  <NuxtLink to="/" title="Business">
                    <div class="py-1 hover:text-gray-300 hover:underline flex justify-center md:justify-start">
                      Business
                    </div>
                  </NuxtLink>
                </li>
                <li class="h-12 sm:h-auto flex justify-start items-center">
                  <NuxtLink to="/" title="Leggi e Normative">
                    <div class="py-1 hover:text-gray-300 hover:underline flex justify-center md:justify-start">
                      Leggi e Normative
                    </div>
                  </NuxtLink>
                </li>
              </ul>
            </div>
            <div v-if="false" class="flex flex-col gap-4">
              <h2 class="text-sm text-[#E95097] uppercase flex justify-center font-semibold md:justify-start">
                Energia
              </h2>
              <ul class="flex flex-col gap-1 font-medium text-gray-500">
                <li class="h-12 sm:h-auto flex justify-start items-center">
                  <NuxtLink to="/" title="Luce privati">
                    <div class="py-1 hover:text-gray-300 hover:underline flex justify-center md:justify-start">
                      Luce privati
                    </div>
                  </NuxtLink>
                </li>
                <li class="h-12 sm:h-auto flex justify-start items-center">
                  <NuxtLink to="/" title="Gas privati">
                    <div class="py-1 hover:text-gray-300 hover:underline flex justify-center md:justify-start">
                      Gas privati
                    </div>
                  </NuxtLink>
                </li>
                <li class="h-12 sm:h-auto flex justify-start items-center">
                  <NuxtLink to="/" title="Luce aziende">
                    <div class="py-1 hover:text-gray-300 hover:underline flex justify-center md:justify-start">
                      Luce aziende
                    </div>
                  </NuxtLink>
                </li>
                <li class="h-12 sm:h-auto flex justify-start items-center">
                  <NuxtLink to="/" title="Gas aziende">
                    <div class="py-1 hover:text-gray-300 hover:underline flex justify-center md:justify-start">
                      Gas aziende
                    </div>
                  </NuxtLink>
                </li>
              </ul>
            </div>
            <div v-if="false" class="flex flex-col gap-4">
              <h2 class="text-sm text-[#1FE5F2] uppercase flex justify-center font-semibold md:justify-start">
                Internet
              </h2>
              <ul class="flex flex-col gap-1 font-medium text-gray-500">
                <li class="h-12 sm:h-auto flex justify-start items-center">
                  <NuxtLink to="/" title="Fibra e ADSL privati">
                    <div class="py-1 hover:text-gray-300 hover:underline flex justify-center md:justify-start">
                      Fibra e ADSL privati
                    </div>
                  </NuxtLink>
                </li>
                <li class="h-12 sm:h-auto flex justify-start items-center">
                  <NuxtLink to="/" title="Fibra e ADSL aziende">
                    <div class="py-1 hover:text-gray-300 hover:underline flex justify-center md:justify-start">
                      Fibra e ADSL aziende
                    </div>
                  </NuxtLink>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- pb-20: spazio libero sotto l'ultima riga per l'icona fissa di Cookiebot
         (48px a 10px dal bordo in basso a sinistra), che altrimenti copre il testo -->
    <div class="mx-auto w-full px-4 pt-4 pb-20 bg-slate-950">
      <span class="flex flex-1 text-slate-400 text-xs">
        <span class="text-xs align-top">*</span>
        <SafeHtml v-if="footer?.last" :raw-html="footer?.last" tag="span" :link-class="darkLinkClass" />
      </span>
    </div>
  </footer>
</template>

<style scoped>
/* WCAG 1.4.3 — active/exact-active links on slate-950 background: violet-300 (#c4b5fd) = 11.3:1 contrast.
   main.scss applies its own color directly to `.router-link-exact-active > div/span/a` (not just the
   link itself), which as an explicit match on the div always wins over this rule's inherited color if
   we only target the <a> — so the div/span/a descendants need to be targeted here too. */
:deep(.router-link-active),
:deep(.router-link-exact-active),
:deep(.router-link-active > div),
:deep(.router-link-exact-active > div),
:deep(.router-link-active > span),
:deep(.router-link-exact-active > span),
:deep(.router-link-active > a),
:deep(.router-link-exact-active > a) {
  color: #c4b5fd;
}
</style>
